import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Star, ExternalLink, Loader2, Copy, CornerDownLeft, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const WinStoriesDialog = ({ open, onOpenChange, insertTarget, onInsert }) => {
  const [stories, setStories] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setStories(null);
    axios.get(`${API_URL}/api/rfp/response/win-stories`, { withCredentials: true })
      .then(res => setStories(res.data?.stories || []))
      .catch(() => setStories([]));
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = (stories || []).filter(s => !q
    || s.question.toLowerCase().includes(q) || s.answer.toLowerCase().includes(q)
    || s.topic.toLowerCase().includes(q) || s.project_name.toLowerCase().includes(q));
  const groups = {};
  filtered.forEach(s => { (groups[s.topic] = groups[s.topic] || []).push(s); });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rfp-win-stories-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] flex items-center gap-2">
            <Star className="w-4 h-4 fill-amber-400 text-amber-500" />
            Win Stories{stories ? ` — ${stories.length}` : ''}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-slate-500">
          Starred answers from past RFPs, grouped by topic. Answers from won deals are suggested first
          when drafting similar questions in future responses.
        </p>
        {insertTarget && (
          <p className="text-[11px] text-cyan-800 bg-cyan-50 border border-cyan-100 rounded-md px-2.5 py-1.5"
            data-testid="rfp-win-stories-insert-banner">
            <CornerDownLeft className="w-3 h-3 inline mr-1" />
            Pick a story to insert into <b>Q{insertTarget.idx + 1}</b>: {insertTarget.question?.slice(0, 120)}
          </p>
        )}
        <Input placeholder="Search question, answer, topic or RFP…" value={search}
          onChange={e => setSearch(e.target.value)} className="h-8 text-xs"
          data-testid="rfp-win-stories-search" />
        {stories === null ? (
          <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-6" />
        ) : filtered.length === 0 ? (
          <p className="text-xs text-slate-400 py-6 text-center" data-testid="rfp-win-stories-empty">
            {stories.length === 0
              ? 'No win stories yet — star your strongest answers on any RFP (the ☆ button on a question) to build the library.'
              : 'No win stories match that search.'}
          </p>
        ) : (
          Object.entries(groups).map(([topic, items]) => (
            <div key={topic} className="space-y-1.5" data-testid={`rfp-win-stories-topic-${topic}`}>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{topic} · {items.length}</p>
              {items.map((s, i) => (
                <div key={`${s.project_id}-${i}`}
                  className="border border-slate-200 rounded-md px-3 py-2 space-y-1 bg-white"
                  data-testid={`rfp-win-story-${s.project_id}-${i}`}>
                  <div className="flex items-start gap-2">
                    <p className="text-xs font-semibold text-[#0F2040] flex-1 min-w-0">{s.question}</p>
                    {s.won && (
                      <Badge variant="outline" className="text-[8px] uppercase font-bold bg-emerald-100 text-emerald-700 border-emerald-300 shrink-0">won</Badge>
                    )}
                    {s.stale && (
                      <Badge variant="outline" className="text-[8px] bg-amber-50 text-amber-700 border-amber-300 shrink-0"
                        title={`Starred over a year ago${s.starred_at ? ` (${s.starred_at.slice(0, 10)})` : ''} — re-check this answer is still accurate before reuse`}
                        data-testid={`rfp-win-story-stale-${s.project_id}-${i}`}>
                        <Clock className="w-2.5 h-2.5 mr-0.5" />stale &gt;1y
                      </Badge>
                    )}
                    {s.client_score && (
                      <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200 shrink-0"
                        title="Client score at close">{s.client_score}</Badge>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-600 line-clamp-3">{s.answer}</p>
                  <div className="flex items-center gap-2 text-[10px] text-slate-400">
                    <span className="truncate">{s.project_name}{s.client_name ? ` · ${s.client_name}` : ''}</span>
                    {s.starred_by && <span>· starred by {s.starred_by.split('@')[0]}</span>}
                    <span className="flex-1" />
                    {insertTarget && onInsert ? (
                      <Button size="sm" variant="outline" onClick={() => onInsert(s)}
                        className="h-5 text-[9px] px-1.5 text-cyan-700 border-cyan-200 hover:bg-cyan-50 shrink-0"
                        data-testid={`rfp-win-story-insert-${s.project_id}-${i}`}>
                        <CornerDownLeft className="w-2.5 h-2.5 mr-0.5" />Insert
                      </Button>
                    ) : (
                      <button onClick={async () => { try { await navigator.clipboard.writeText(s.answer); toast.success('Answer copied'); } catch { toast.error('Could not copy'); } }}
                        className="text-slate-400 font-semibold flex items-center gap-0.5 hover:text-[#0F2040] shrink-0"
                        title="Copy this answer to the clipboard"
                        data-testid={`rfp-win-story-copy-${s.project_id}-${i}`}>
                        <Copy className="w-2.5 h-2.5" />Copy
                      </button>
                    )}
                    <button onClick={() => { onOpenChange(false); navigate(`/rfp-response?project=${s.project_id}`); }}
                      className="text-[#4ABFCF] font-semibold flex items-center gap-0.5 hover:underline shrink-0"
                      data-testid={`rfp-win-story-open-${s.project_id}-${i}`}>
                      <ExternalLink className="w-2.5 h-2.5" />Open RFP
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};
